<template>
  <div class="vehicles-chart">
    <VueApexCharts
      type="radialBar"
      :height="chartHeight"
      :options="chartOptions"
      :series="series"
    ></VueApexCharts>
  </div>
</template>

<script setup lang="ts">
import VueApexCharts from 'vue3-apexcharts'
import { ref, watch, onMounted, onUnmounted } from 'vue'
import type { IVehicleAnalyticsAggregated } from '@/components/dashboard/types'

const props = defineProps<{
  data: IVehicleAnalyticsAggregated | null
  dataToCompare: IVehicleAnalyticsAggregated | null
}>()

// Responsive chart height
const chartHeight = ref(350)
const updateChartHeight = () => {
  if (window.innerWidth <= 599) {
    chartHeight.value = 250
  } else if (window.innerWidth <= 959) {
    chartHeight.value = 300
  } else {
    chartHeight.value = 350
  }
}

const updateChartOptions = () => {
  chartOptions.value.title.style.fontSize =
    window.innerWidth <= 599 ? '16px' : '18px'
  chartOptions.value.subtitle.style.fontSize =
    window.innerWidth <= 599 ? '12px' : '14px'
  chartOptions.value.plotOptions.radialBar.hollow.margin =
    window.innerWidth <= 599 ? 35 : 45
  chartOptions.value.plotOptions.radialBar.dataLabels.name.fontSize =
    window.innerWidth <= 599 ? '14px' : '16px'
  chartOptions.value.plotOptions.radialBar.dataLabels.name.offsetY =
    window.innerWidth <= 599 ? 15 : 20
  chartOptions.value.plotOptions.radialBar.dataLabels.value.fontSize =
    window.innerWidth <= 599 ? '20px' : '24px'
  chartOptions.value.plotOptions.radialBar.dataLabels.value.offsetY =
    window.innerWidth <= 599 ? -15 : -20
  chartOptions.value.plotOptions.radialBar.dataLabels.total.fontSize =
    window.innerWidth <= 599 ? '12px' : '14px'
}

onMounted(() => {
  updateChartHeight()
  updateChartOptions()
  window.addEventListener('resize', () => {
    updateChartHeight()
    updateChartOptions()
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateChartHeight)
})

const currentTotal = ref<number>(0)
const previousTotal = ref<number>(0)
const series = ref<number[]>([0, 0]) // Initialize with two values

const chartOptions = ref({
  chart: {
    type: 'radialBar',
    height: 350,
    toolbar: {
      show: false, // Hide toolbar on mobile for cleaner look
    },
  },
  title: {
    text: 'CTR Comparison',
    align: 'center',
    style: {
      fontSize: window.innerWidth <= 599 ? '16px' : '18px',
      fontWeight: 600,
    },
  },
  subtitle: {
    text: 'Last Month vs This Month',
    align: 'center',
    style: {
      fontSize: window.innerWidth <= 599 ? '12px' : '14px',
    },
  },
  plotOptions: {
    radialBar: {
      startAngle: -90,
      endAngle: 90,
      hollow: {
        margin: window.innerWidth <= 599 ? 35 : 45,
        size: '50%',
      },
      track: {
        background: '#e7e7e7',
        strokeWidth: '97%',
        margin: 5,
      },
      dataLabels: {
        name: {
          show: true,
          fontSize: window.innerWidth <= 599 ? '14px' : '16px',
          offsetY: window.innerWidth <= 599 ? 15 : 20,
          color: '#888',
        },
        value: {
          show: true,
          fontSize: window.innerWidth <= 599 ? '20px' : '24px',
          offsetY: window.innerWidth <= 599 ? -15 : -20,
          color: '#111',
          formatter: function (val: number) {
            return Number(val).toFixed(2) + '%'
          },
        },
        total: {
          show: true,
          label: 'This Month',
          color: '#888',
          fontSize: window.innerWidth <= 599 ? '12px' : '14px',
          formatter: function () {
            return currentTotal.value.toFixed(2) + '%'
          },
        },
      },
    },
  },
  colors: ['#4CAF50', '#A9A9A9'], // First color for inner arc, second for outer
  labels: ['This Month', 'Last Month'],
  stroke: {
    lineCap: 'round',
  },
  responsive: [
    {
      breakpoint: 959,
      options: {
        chart: {
          height: 300,
        },
        title: {
          style: {
            fontSize: '16px',
          },
        },
        subtitle: {
          style: {
            fontSize: '12px',
          },
        },
        plotOptions: {
          radialBar: {
            hollow: {
              margin: 40,
            },
            dataLabels: {
              name: {
                fontSize: '14px',
                offsetY: 18,
              },
              value: {
                fontSize: '22px',
                offsetY: -18,
              },
              total: {
                fontSize: '12px',
              },
            },
          },
        },
      },
    },
    {
      breakpoint: 599,
      options: {
        chart: {
          height: 250,
        },
        title: {
          style: {
            fontSize: '14px',
          },
        },
        subtitle: {
          style: {
            fontSize: '11px',
          },
        },
        plotOptions: {
          radialBar: {
            hollow: {
              margin: 30,
            },
            dataLabels: {
              name: {
                fontSize: '12px',
                offsetY: 12,
              },
              value: {
                fontSize: '18px',
                offsetY: -12,
              },
              total: {
                fontSize: '10px',
              },
            },
          },
        },
      },
    },
  ],
})

watch(
  () => [props.data, props.dataToCompare],
  ([newData, newDataToCompare]) => {
    let currentCTR = newData?.ctr ?? 0
    let previousCTR = newDataToCompare?.ctr ?? 0

    // Set the values (ctr is already a number in IVehicleAnalyticsAggregated)
    currentTotal.value = currentCTR
    previousTotal.value = previousCTR

    // Update both values at once
    series.value = [currentTotal.value, previousTotal.value]
  },
  { immediate: true },
)
</script>
